import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, Collapse, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, FormControl, Autocomplete, Stack, Slider, Switch, FormControlLabel,
  Alert, Paper, InputAdornment, Divider,
  ToggleButtonGroup, ToggleButton, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import type { IScoringRule, IScoringRuleCondition, IScoringRuleConsequence, IScoringRuleBranchMeta, ICheckPlus, ICategory, IScanTemplate } from '../../../shared/types.js';

/* ── Template types ──────────────────────────────────────── */

interface IScanTemplateWithRules extends IScanTemplate {
  template_scoring_rules?: Array<{
    template_id: number;
    scoring_rule_id: string;
    scoring_rule?: IScoringRule;
  }>;
}

/* ── Form types ──────────────────────────────────────────── */

interface RuleConditionForm {
  check_id: string;
  expected_rating: string;
}

interface RuleConsequenceForm {
  category_id: string;
  impact_direction: '+' | '-';
  impact_percent: number;
}

interface BranchForm {
  /**
   * ID des bestehenden Falls. Wird beim Speichern zurueckgeschickt, damit der
   * Erklaertext (Tabelle scoring_rule_branches) am Fall haengen bleibt. `null`
   * bei neu angelegten Faellen — die ID vergibt dann der Server.
   */
  branch_id: string | null;
  explanation_active: boolean;
  explanation: string;
  /** Handlungsempfehlung dieses Falls — eigener Text, nicht der Erklaertext. */
  recommendation: string;
  /** Die Handlungsempfehlung dieses Falls darf als Quick Win auf die Scorecard. */
  quick_win: boolean;
  conditions: RuleConditionForm[];
  consequences: RuleConsequenceForm[];
}

/* ── Helpers ─────────────────────────────────────────────── */

function buildBranchForms(
  conditions: IScoringRuleCondition[],
  consequences: IScoringRuleConsequence[],
  branches: IScoringRuleBranchMeta[] = [],
): BranchForm[] {
  if (conditions.length === 0) return [defaultBranch()];
  const byId = new Map(branches.map((b) => [b.branch_id, b]));
  const branchMap = new Map<string, BranchForm>();
  const ensure = (key: string): BranchForm => {
    let entry = branchMap.get(key);
    if (!entry) {
      const meta = key === '__legacy__' ? undefined : byId.get(key);
      entry = {
        branch_id: key === '__legacy__' ? null : key,
        explanation_active: meta?.explanation_active ?? false,
        explanation: meta?.explanation ?? '',
        recommendation: meta?.recommendation ?? '',
        quick_win: meta?.quick_win ?? false,
        conditions: [],
        consequences: [],
      };
      branchMap.set(key, entry);
    }
    return entry;
  };
  for (const c of conditions) {
    const key = c.branch_id ?? '__legacy__';
    ensure(key).conditions.push({ check_id: c.check_id, expected_rating: c.expected_rating });
  }
  for (const c of consequences) {
    const key = c.branch_id ?? '__legacy__';
    ensure(key).consequences.push({
      category_id: c.category_id,
      impact_direction: c.impact_direction as '+' | '-',
      impact_percent: Number(c.impact_percent),
    });
  }
  const result = Array.from(branchMap.values());
  return result.length > 0 ? result : [defaultBranch()];
}

/** Groups conditions/consequences by branch_id for display, preserving all association data. */
function groupForDisplay(
  conditions: IScoringRuleCondition[],
  consequences: IScoringRuleConsequence[],
): Array<{ conditions: IScoringRuleCondition[]; consequences: IScoringRuleConsequence[] }> {
  if (conditions.length === 0 && consequences.length === 0) return [{ conditions: [], consequences: [] }];
  const keys: string[] = [];
  const map = new Map<string, { conditions: IScoringRuleCondition[]; consequences: IScoringRuleConsequence[] }>();
  const ensure = (key: string) => {
    if (!map.has(key)) { keys.push(key); map.set(key, { conditions: [], consequences: [] }); }
  };
  for (const c of conditions) { const k = c.branch_id ?? '__legacy__'; ensure(k); map.get(k)!.conditions.push(c); }
  for (const c of consequences) { const k = c.branch_id ?? '__legacy__'; ensure(k); map.get(k)!.consequences.push(c); }
  return keys.map((k) => map.get(k)!);
}

function defaultBranch(): BranchForm {
  return {
    branch_id: null,
    explanation_active: false,
    explanation: '',
    recommendation: '',
    quick_win: false,
    conditions: [{ check_id: '', expected_rating: 'R' }],
    consequences: [{ category_id: '', impact_direction: '-', impact_percent: 10 }],
  };
}

export default function ScoringEnginePage() {
  const { user } = useAuth();
  // Der "Entwickler Only"-Switch darf ausschliesslich im Entwicklermodus sichtbar sein
  const devMode = user?.dev_mode === true;
  const [rules, setRules] = useState<IScoringRule[]>([]);
  const [checks, setChecks] = useState<ICheckPlus[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [formDevOnly, setFormDevOnly] = useState(false);

  const [viewMode, setViewMode] = useState<'list' | 'template'>(
    () => (localStorage.getItem('scoring_engine_view_mode') as 'list' | 'template') ?? 'list',
  );
  const [templates, setTemplates] = useState<IScanTemplateWithRules[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  const [form, setForm] = useState<{
    name: string;
    description: string;
    impact_level: number;
    is_showstopper: boolean;
    branches: BranchForm[];
  }>({
    name: '',
    description: '',
    impact_level: 3,
    is_showstopper: false,
    branches: [defaultBranch()],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, checksRes, catsRes] = await Promise.all([
        api.get<IScoringRule[]>('/config-plus/scoring-rules'),
        api.get<{ checks: ICheckPlus[] } | ICheckPlus[]>('/config-plus/checks'),
        api.get<{ categories: ICategory[] }>('/config/categories'),
      ]);
      if (rulesRes.success) setRules(rulesRes.data);
      if (checksRes.success) {
        const rawChecks = checksRes.data;
        const list = Array.isArray(rawChecks) ? rawChecks : rawChecks?.checks;
        setChecks(Array.isArray(list) ? list : []);
      }
      if (catsRes.success) setCategories(catsRes.data.categories.filter((c) => c.engine_mode === 'rule_engine'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (viewMode === 'template' && !templatesLoaded) {
      api.get<{ templates: IScanTemplateWithRules[]; score_card_html_template_persistable: boolean }>(
        '/config/scan-templates',
      ).then((res) => {
        if (res.success) setTemplates(res.data.templates);
        setTemplatesLoaded(true);
      }).catch(() => setTemplatesLoaded(true));
    }
  }, [viewMode, templatesLoaded]);

  const buildPayload = () => ({
    name: form.name,
    description: form.description || null,
    impact_level: form.impact_level,
    is_showstopper: form.is_showstopper,
    dev_only: formDevOnly,
    branches: form.branches,
  });

  const handleCreate = async () => {
    try {
      const res = await api.post<IScoringRule>('/config-plus/scoring-rules', buildPayload());
      if (res.success) {
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        setError(res.error?.message ?? 'Speichern fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState<IScoringRule | null>(null);
  const [deleteBlockedBy, setDeleteBlockedBy] = useState<string[]>([]);

  const handleDeleteClick = async (rule: IScoringRule) => {
    try {
      const res = await api.get<{ templates: Array<{ display_name: string }> }>(
        `/config-plus/scoring-rules/${rule.id}/templates`,
      );
      setDeleteBlockedBy(res.success ? res.data.templates.map((t) => t.display_name) : []);
    } catch {
      setDeleteBlockedBy([]);
    }
    setDeleteTarget(rule);
  };

  const handleDelete = async (id: string) => {
    await api.delete(`/config-plus/scoring-rules/${id}`);
    setDeleteTarget(null);
    fetchData();
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      await api.patch(`/config-plus/scoring-rules/${id}/toggle-active`, { is_active: !currentActive });
      setRules((prev) => prev.map((r) => r.id === id ? { ...r, active: !currentActive } : r));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleEdit = (rule: IScoringRule) => {
    setForm({
      name: rule.name,
      description: rule.description ?? '',
      impact_level: rule.impact_level,
      is_showstopper: rule.is_showstopper,
      branches: buildBranchForms(rule.conditions ?? [], rule.consequences ?? [], rule.branches ?? []),
    });
    setFormDevOnly(rule.dev_only ?? false);
    setEditingRuleId(rule.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (editingRuleId) {
      try {
        const res = await api.patch(`/config-plus/scoring-rules/${editingRuleId}`, buildPayload());
        if (res.success) {
          setDialogOpen(false);
          setEditingRuleId(null);
          resetForm();
          fetchData();
        } else {
          setError('Speichern fehlgeschlagen');
        }
      } catch (e) {
        setError((e as Error).message);
      }
    } else {
      await handleCreate();
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', impact_level: 3, is_showstopper: false, branches: [defaultBranch()] });
    setFormDevOnly(false);
  };

  /* ── Branch mutation helpers ─────────────────────────────── */

  const updateBranch = (branchIdx: number, updater: (b: BranchForm) => BranchForm) =>
    setForm((prev) => ({
      ...prev,
      branches: prev.branches.map((b, i) => i === branchIdx ? updater(b) : b),
    }));

  const addBranch = () =>
    setForm((prev) => ({ ...prev, branches: [...prev.branches, defaultBranch()] }));

  const removeBranch = (idx: number) =>
    setForm((prev) => ({ ...prev, branches: prev.branches.filter((_, i) => i !== idx) }));

  const addConditionToBranch = (branchIdx: number) =>
    updateBranch(branchIdx, (b) => ({ ...b, conditions: [...b.conditions, { check_id: '', expected_rating: 'R' }] }));

  const addConsequenceToBranch = (branchIdx: number) =>
    updateBranch(branchIdx, (b) => ({ ...b, consequences: [...b.consequences, { category_id: '', impact_direction: '-' as const, impact_percent: 10 }] }));

  const updateCondition = (branchIdx: number, condIdx: number, field: keyof RuleConditionForm, value: string) =>
    updateBranch(branchIdx, (b) => ({
      ...b,
      conditions: b.conditions.map((c, i) => i === condIdx ? { ...c, [field]: value } : c),
    }));

  const removeCondition = (branchIdx: number, condIdx: number) =>
    updateBranch(branchIdx, (b) => ({ ...b, conditions: b.conditions.filter((_, i) => i !== condIdx) }));

  const updateConsequence = (branchIdx: number, consIdx: number, updates: Partial<RuleConsequenceForm>) =>
    updateBranch(branchIdx, (b) => ({
      ...b,
      consequences: b.consequences.map((c, i) => i === consIdx ? { ...c, ...updates } : c),
    }));

  const removeConsequence = (branchIdx: number, consIdx: number) =>
    updateBranch(branchIdx, (b) => ({ ...b, consequences: b.consequences.filter((_, i) => i !== consIdx) }));

  /* ── Display helpers ─────────────────────────────────────── */

  const ratingLabel = (r: string) =>
    r === 'G' ? 'Grün' : r === 'Y' ? 'Gelb' : r === '!G' ? 'Nicht Grün' : r === 'Y,R' ? 'Gelb/Rot' : 'Rot';

  const conditionBorderColor = (r: string) => {
    if (r === 'G') return colors.scoreGreen;
    if (r === 'Y') return colors.scoreYellow;
    if (r === 'R') return colors.scoreRed;
    return '#9e9e9e';
  };

  const isFormInvalid = !form.name ||
    form.branches.some((b) => b.conditions.some((c) => !c.check_id) || b.consequences.some((c) => !c.category_id));

  const filteredRules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules
      .filter((r) => {
        if (!showInactive && !r.active) return false;
        if (!q) return true;
        return r.name.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (a.active === b.active) return a.name.localeCompare(b.name);
        return a.active ? -1 : 1;
      });
  }, [rules, search, showInactive]);

  if (loading) return <Typography>Lade Scoring-Regeln...</Typography>;

  /* ── Rule card renderer ──────────────────────────────────── */

  const renderRuleCard = (rule: IScoringRule) => {
    const displayBranches = groupForDisplay(rule.conditions ?? [], rule.consequences ?? []);
    const isMultiBranch = displayBranches.length > 1;
    return (
      <Card key={rule.id} sx={{ borderRadius: 3, border: rule.is_showstopper ? `2px solid ${colors.scoreRed}` : undefined }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setExpandedId(expandedId === rule.id ? null : rule.id)}>
              {expandedId === rule.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography sx={{ fontWeight: 600, flex: 1, opacity: rule.active ? 1 : 0.5 }}>{rule.name}</Typography>
            <Chip label={`Impact ${rule.impact_level}`} size="small" />
            {rule.is_showstopper && <Chip label="Showstopper" size="small" color="error" />}
            {isMultiBranch && (
              <Chip label={`${displayBranches.length} Fälle`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
            )}
            <Switch
              size="small"
              checked={rule.active}
              onChange={() => handleToggleActive(rule.id, rule.active)}
              title={rule.active ? 'Deaktivieren' : 'Aktivieren'}
            />
            <IconButton size="small" onClick={() => handleEdit(rule)}>
              <SettingsIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(rule)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          <Collapse in={expandedId === rule.id}>
            <Box sx={{ mt: 2, pl: 2 }}>
              {rule.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {rule.description}
                </Typography>
              )}
              <Stack spacing={1.5}>
                {displayBranches.map((branch, bi) => (
                  <Box key={bi}>
                    {bi > 0 && (
                      <Divider sx={{ my: 0.5 }}>
                        <Chip label="SONST WENN" size="small" sx={{ fontSize: 10, height: 18, bgcolor: '#f0f0f0' }} />
                      </Divider>
                    )}
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                        Bedingungen (WENN)
                      </Typography>
                      <Stack spacing={1}>
                        {branch.conditions.map((c, ci) => (
                          <Box key={ci}>
                            {ci > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75, ml: 1 }}>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                                <Chip label="UND" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#f5f5f5' }} />
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                              </Box>
                            )}
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.25, borderRadius: 2,
                                borderColor: conditionBorderColor(c.expected_rating),
                                borderStyle: 'dashed', borderWidth: 1.5,
                                display: 'flex', alignItems: 'center', gap: 1.5,
                              }}
                            >
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: conditionBorderColor(c.expected_rating), flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{c.check?.title ?? c.check_plus?.title ?? c.check_id}</strong>
                              </Typography>
                              <Typography variant="body2" color="text.secondary">Ergebnis ist</Typography>
                              <Chip
                                label={ratingLabel(c.expected_rating)}
                                size="small"
                                sx={{ bgcolor: conditionBorderColor(c.expected_rating), color: '#fff', fontWeight: 700 }}
                              />
                            </Paper>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                        Konsequenzen (DANN)
                      </Typography>
                      <Stack spacing={1}>
                        {branch.consequences.map((c, ci) => (
                          <Box key={ci}>
                            {ci > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75, ml: 1 }}>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                                <Chip label="UND" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#f5f5f5' }} />
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                              </Box>
                            )}
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.25, borderRadius: 2,
                                borderColor: c.impact_direction === '+' ? colors.scoreGreen : colors.scoreRed,
                                borderStyle: 'dashed', borderWidth: 1.5,
                                display: 'flex', alignItems: 'center', gap: 1.5,
                              }}
                            >
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.impact_direction === '+' ? colors.scoreGreen : colors.scoreRed, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>{c.category?.display_name ?? c.category_id}</strong>
                              </Typography>
                              <Chip
                                label={`Score ${c.impact_direction}${c.impact_percent}%`}
                                size="small"
                                sx={{ bgcolor: c.impact_direction === '+' ? colors.scoreGreen : colors.scoreRed, color: '#fff', fontWeight: 700 }}
                              />
                            </Paper>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Scoring Engine</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Scoring-Regel suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
          label={<Typography variant="body2">Inaktive einblenden</Typography>}
          sx={{ ml: 0 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' }, whiteSpace: 'nowrap' }}
        >
          Neue Regel
        </Button>
      </Box>

      {/* ── View mode toggle ──────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_e, val: 'list' | 'template' | null) => {
            if (val !== null) {
              setViewMode(val);
              localStorage.setItem('scoring_engine_view_mode', val);
            }
          }}
          sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.23)',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              px: 2.5,
              py: 0.75,
              border: 'none',
              '&:first-of-type': { borderRadius: '24px 0 0 24px' },
              '&:last-of-type': { borderRadius: '0 24px 24px 0' },
              '&.Mui-selected': {
                bgcolor: colors.black,
                color: colors.lime,
                fontWeight: 600,
                '&:hover': { bgcolor: colors.black85 },
              },
            },
          }}
        >
          <ToggleButton value="list">Liste</ToggleButton>
          <ToggleButton value="template">Template</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Liste / Template view ─────────────────────────────── */}
      {viewMode === 'list' ? (
        <Stack spacing={2}>
          {filteredRules.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {search ? 'Keine Treffer für die Suche.' : 'Keine Scoring-Regeln vorhanden.'}
            </Typography>
          ) : filteredRules.map((rule) => renderRuleCard(rule))}
        </Stack>
      ) : (
        <Box>
          {/* Unassigned — always first */}
          {(() => {
            const assignedIds = new Set(
              templates.flatMap((t) => (t.template_scoring_rules ?? []).map((r) => r.scoring_rule_id)),
            );
            const unassigned = filteredRules.filter((r) => !assignedIds.has(r.id));
            if (unassigned.length === 0 && templatesLoaded) return null;
            return (
              <Accordion defaultExpanded sx={{ mb: 1, overflow: 'hidden', '&:before': { display: 'none' }, '&.MuiPaper-root': { borderTopLeftRadius: 24, borderTopRightRadius: 24, borderBottomLeftRadius: 24, borderBottomRightRadius: 24 } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>Ohne Template-Zuordnung</Typography>
                    <Chip label={unassigned.length} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {unassigned.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Keine Regeln ohne Template-Zuordnung.</Typography>
                  ) : (
                    <Stack spacing={2}>{unassigned.map((rule) => renderRuleCard(rule))}</Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })()}

          {/* One accordion per template — only config_plus templates */}
          {templates.filter((t) => t.source === 'config_plus').map((template) => {
            const templateRuleIds = new Set(
              (template.template_scoring_rules ?? []).map((r) => r.scoring_rule_id),
            );
            const templateRules = filteredRules.filter((r) => templateRuleIds.has(r.id));
            const q = search.trim().toLowerCase();
            if (q && templateRules.length === 0) return null;
            const templateCategories = template.template_categories ?? [];

            return (
              <Accordion key={template.id} sx={{ mb: 1, overflow: 'hidden', '&:before': { display: 'none' }, '&.MuiPaper-root': { borderTopLeftRadius: 24, borderTopRightRadius: 24, borderBottomLeftRadius: 24, borderBottomRightRadius: 24 } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>{template.display_name}</Typography>
                    <Chip label={templateRules.length} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {templateRules.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Keine Scoring-Regeln für dieses Template.</Typography>
                  ) : templateCategories.length === 0 ? (
                    <Stack spacing={2}>{templateRules.map((rule) => renderRuleCard(rule))}</Stack>
                  ) : (
                    <Box>
                      {templateCategories.map((tc) => {
                        const catRules = templateRules.filter((r) =>
                          (r.consequences ?? []).some((c) => c.category_id === tc.category_id),
                        );
                        if (catRules.length === 0) return null;
                        return (
                          <Accordion key={tc.category_id} sx={{ mb: 0.5, overflow: 'hidden', '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.12)', '&.MuiPaper-root': { borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 16, borderBottomRightRadius: 16 } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {tc.category?.display_name ?? tc.category_id}
                                </Typography>
                                <Chip label={catRules.length} size="small" />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={2}>{catRules.map((rule) => renderRuleCard(rule))}</Stack>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {!templatesLoaded && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Lade Templates…
            </Typography>
          )}
        </Box>
      )}

      {/* ── Create/Edit Dialog ──────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingRuleId(null); resetForm(); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingRuleId ? 'Scoring-Regel bearbeiten' : 'Neue Scoring-Regel'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Beschreibung"
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom>Impact-Level: {form.impact_level}</Typography>
                <Slider
                  min={1} max={5}
                  value={form.impact_level}
                  onChange={(_, v) => setForm({ ...form, impact_level: v as number })}
                  marks step={1}
                />
              </Box>
              <FormControlLabel
                control={<Switch checked={form.is_showstopper} onChange={(e) => setForm({ ...form, is_showstopper: e.target.checked })} />}
                label="Showstopper"
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Datenquelle: checks_plus (Konfiguration+ Check Library)
            </Typography>

            {/* ── Branches ─────────────────────────────────────────── */}
            {form.branches.map((branch, branchIdx) => (
              <Box key={branchIdx}>
                {branchIdx > 0 && (
                  <Divider sx={{ my: 1 }}>
                    <Chip label="SONST WENN" size="small" sx={{ fontSize: 11, height: 20, bgcolor: '#f0f0f0' }} />
                  </Divider>
                )}

                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: branchIdx % 2 === 0 ? undefined : '#fafafa' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    {form.branches.length > 1 && (
                      <Chip label={`Fall ${branchIdx + 1}`} size="small" sx={{ mr: 1, fontWeight: 700 }} />
                    )}
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>WENN (Bedingungen):</Typography>
                    {form.branches.length > 1 && (
                      <IconButton size="small" onClick={() => removeBranch(branchIdx)} title="Diesen Fall entfernen">
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>

                  {/* Conditions */}
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                    <Stack spacing={1}>
                      {branch.conditions.map((cond, condIdx) => (
                        <Box key={condIdx}>
                          {condIdx > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75 }}>
                              <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                              <Chip label="UND" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#f5f5f5' }} />
                              <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                            </Box>
                          )}
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.25, borderRadius: 2,
                              borderColor: conditionBorderColor(cond.expected_rating),
                              borderStyle: 'dashed', borderWidth: 1.5,
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Autocomplete
                                options={checks}
                                getOptionLabel={(option) => option.title}
                                value={checks.find((c) => c.id === cond.check_id) ?? null}
                                onChange={(_e, value) => updateCondition(branchIdx, condIdx, 'check_id', value?.id ?? '')}
                                size="small"
                                sx={{ flex: '1 1 220px', minWidth: 200 }}
                                noOptionsText="Kein Check gefunden"
                                openOnFocus
                                slotProps={{ popper: { placement: 'bottom-start', modifiers: [{ name: 'flip', enabled: false }] } }}
                                ListboxProps={{ sx: { maxHeight: 200 } }}
                                renderInput={(params) => <TextField {...params} label="Check" />}
                              />
                              <Typography variant="body2" sx={{ flexShrink: 0 }}>Ergebnis ist</Typography>
                              <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                  value={cond.expected_rating}
                                  MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { maxHeight: 200 } } }}
                                  onChange={(e) => updateCondition(branchIdx, condIdx, 'expected_rating', e.target.value)}
                                >
                                  <MenuItem value="G"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.scoreGreen }} />Grün</Box></MenuItem>
                                  <MenuItem value="Y"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.scoreYellow }} />Gelb</Box></MenuItem>
                                  <MenuItem value="R"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.scoreRed }} />Rot</Box></MenuItem>
                                  <MenuItem value="!G"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#9e9e9e' }} />Nicht Grün</Box></MenuItem>
                                  <MenuItem value="Y,R"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#9e9e9e' }} />Gelb oder Rot</Box></MenuItem>
                                </Select>
                              </FormControl>
                              {branch.conditions.length > 1 && (
                                <IconButton size="small" onClick={() => removeCondition(branchIdx, condIdx)}>
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Paper>
                        </Box>
                      ))}
                    </Stack>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => addConditionToBranch(branchIdx)}>
                      + UND-Bedingung hinzufügen
                    </Button>
                  </Paper>

                  {/* Consequences */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>DANN (Konsequenzen):</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={1}>
                      {branch.consequences.map((cons, consIdx) => (
                        <Box key={consIdx}>
                          {consIdx > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75 }}>
                              <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                              <Chip label="UND" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#f5f5f5' }} />
                              <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                            </Box>
                          )}
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.25, borderRadius: 2,
                              borderColor: cons.impact_direction === '+' ? colors.scoreGreen : colors.scoreRed,
                              borderStyle: 'dashed', borderWidth: 1.5,
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Autocomplete
                                options={categories}
                                getOptionLabel={(option) => option.display_name}
                                value={categories.find((c) => c.id === cons.category_id) ?? null}
                                onChange={(_e, value) => updateConsequence(branchIdx, consIdx, { category_id: value?.id ?? '' })}
                                size="small"
                                sx={{ flex: '1 1 180px', minWidth: 160 }}
                                noOptionsText="Keine Kategorie gefunden"
                                openOnFocus
                                slotProps={{ popper: { placement: 'bottom-start', modifiers: [{ name: 'flip', enabled: false }] } }}
                                ListboxProps={{ sx: { maxHeight: 200 } }}
                                renderInput={(params) => <TextField {...params} label="Kategorie" />}
                              />
                              <Typography variant="body2" sx={{ flexShrink: 0 }}>Score</Typography>
                              <FormControl size="small" sx={{ minWidth: 70 }}>
                                <Select
                                  value={cons.impact_direction}
                                  MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' } }}
                                  onChange={(e) => updateConsequence(branchIdx, consIdx, { impact_direction: e.target.value as '+' | '-' })}
                                  sx={{
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: cons.impact_direction === '+' ? colors.scoreGreen : colors.scoreRed },
                                    color: cons.impact_direction === '+' ? colors.scoreGreen : colors.scoreRed,
                                    fontWeight: 700,
                                  }}
                                >
                                  <MenuItem value="+">+</MenuItem>
                                  <MenuItem value="-">−</MenuItem>
                                </Select>
                              </FormControl>
                              <TextField
                                size="small"
                                type="number"
                                sx={{ width: 80 }}
                                value={cons.impact_percent}
                                onChange={(e) => updateConsequence(branchIdx, consIdx, { impact_percent: Number(e.target.value) })}
                              />
                              <Typography variant="body2" sx={{ flexShrink: 0 }}>%</Typography>
                              {branch.consequences.length > 1 && (
                                <IconButton size="small" onClick={() => removeConsequence(branchIdx, consIdx)}>
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Paper>
                        </Box>
                      ))}
                    </Stack>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => addConsequenceToBranch(branchIdx)}>
                      + Weitere Konsequenz hinzufügen
                    </Button>
                  </Paper>

                  {/* ── Erklaertext dieses Falls ────────────────────── */}
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={branch.explanation_active}
                          onChange={(e) => updateBranch(branchIdx, (b) => ({ ...b, explanation_active: e.target.checked }))}
                        />
                      }
                      label={
                        <Typography variant="subtitle2">
                          Erklärtext ausgeben, wenn dieser Fall zutrifft
                        </Typography>
                      }
                    />
                    {/* Bewusst auch bei ausgeschaltetem Schalter sichtbar: der Text bleibt
                        gespeichert, und man soll sehen, dass er noch da ist. */}
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                      sx={{ mt: 1, ...(branch.explanation_active ? {} : { opacity: 0.6 }) }}
                      value={branch.explanation}
                      onChange={(e) => updateBranch(branchIdx, (b) => ({ ...b, explanation: e.target.value }))}
                      placeholder="Was bedeutet dieser Fall für den Kunden? Dieser Text erscheint in der Auswertung."
                      helperText={
                        branch.explanation_active
                          ? 'Erscheint in der Auswertung unter dieser Regel — nur wenn genau dieser Fall gefeuert hat.'
                          : 'Schalter aus: Der Text bleibt gespeichert, wird in der Auswertung aber nicht angezeigt.'
                      }
                    />
                    {/* ── Handlungsempfehlung dieses Falls (eigener Text, nicht der Erklaertext) ── */}
                    <Typography variant="subtitle2" sx={{ mt: 2.5 }}>
                      Handlungsempfehlung, wenn dieser Fall zutrifft
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                      sx={{ mt: 1 }}
                      value={branch.recommendation}
                      onChange={(e) => updateBranch(branchIdx, (b) => ({ ...b, recommendation: e.target.value }))}
                      placeholder="Was soll der Kunde tun? Leer lassen, wenn es in diesem Fall nichts zu tun gibt."
                      helperText="Erscheint in der Scan-Auswertung unter „Handlungsempfehlungen“ — als Quick Win gewählt auch auf der Scorecard."
                    />
                    {/* Quick Win: die Handlungsempfehlung darüber, kein eigener Text */}
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          size="small"
                          checked={branch.quick_win}
                          onChange={(e) => updateBranch(branchIdx, (b) => ({ ...b, quick_win: e.target.checked }))}
                        />
                      }
                      label={
                        <Typography variant="subtitle2">
                          Als Quick Win werten
                        </Typography>
                      }
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {branch.quick_win && !branch.recommendation.trim()
                        ? 'Wirkt erst mit einer Handlungsempfehlung — ohne Text gibt es nichts für die Scorecard.'
                        : 'Feuert dieser Fall, schlägt das System seine Handlungsempfehlung als Quick Win für die Scorecard vor (höchstens drei je Scan, zuerst je Kategorie einer).'}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addBranch}
              sx={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}
            >
              Weiteren Fall hinzufügen (SONST WENN)
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ flex: 1 }}>
            {devMode && (
              <FormControlLabel
                control={<Switch checked={formDevOnly} onChange={(e) => setFormDevOnly(e.target.checked)} size="small" />}
                label={<Typography variant="body2">Entwickler Only</Typography>}
              />
            )}
          </Box>
          <Button onClick={() => { setDialogOpen(false); setEditingRuleId(null); resetForm(); }}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isFormInvalid}
            sx={{ bgcolor: colors.lime, color: colors.black }}
          >
            {editingRuleId ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete: blocked */}
      <Dialog open={!!deleteTarget && deleteBlockedBy.length > 0} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Regel kann nicht gelöscht werden</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{deleteTarget?.name}</strong> wird noch in folgenden Templates verwendet:
          </Typography>
          <Stack spacing={0.5}>
            {deleteBlockedBy.map((name) => (
              <Typography key={name} variant="body2" sx={{ pl: 1 }}>• {name}</Typography>
            ))}
          </Stack>
          <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
            Entferne die Regel zuerst aus den Templates, bevor du sie löschst.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDeleteTarget(null)}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' } }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete: confirm */}
      <Dialog open={!!deleteTarget && deleteBlockedBy.length === 0} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Scoring-Regel löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du die Regel <strong>{deleteTarget?.name}</strong> wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={() => deleteTarget && handleDelete(deleteTarget.id)}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

